import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useGlobalContext } from "../../GlobalContext";
import TablePagination from "../Utils/TablePagination";
import {
  ClipboardList,
  CheckCircle,
  XCircle,
  IndianRupee,
  Wallet,
  FileDown,
} from "lucide-react";

import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";

// amount formatter
const fmt2 = (num) =>
  Number(num || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const DriverAnalytics = () => {
  const { driverId, username } = useParams();
  const navigate = useNavigate();

  const { baseurl, token, AdminId, setIsUnauthorised } = useGlobalContext();

  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const [reports, setReports] = useState([]);
  const [taskLoading, setTaskLoading] = useState(false);

  const showValue = (val, isAmount = false) => {
    if (val === null || val === undefined) return "N/A";
    if (isAmount) return `₹ ${fmt2(val)}`;
    return val;
  };

  const handleExport = () => {
    if (!reports || reports.length === 0) {
      alert("No reports available to export");
      return;
    }

    exportAnyJsonToExcel({
      data: reports,
      filePrefix: `Driver_Report_${driverId}`,
      sheetName: "Driver Reports",
    });
  };

  // ANALYTICS API
  const fetchDriverAnalytics = async (driverCode) => {
    if (!driverCode) return;

    setLoading(true);
    try {
      const payload = {
        adminId: AdminId,
        driverCode,
        fromDate,
        toDate,
      };

      const res = await axios.post(
        `${baseurl}/api/Driver/v2.0/getDriverAnalytics`,
        payload,
        {
          headers: {
            Authorization: `Basic ${token}`,
          },
        }
      );

      setAnalytics(res.data?.data || null);
    } catch (error) {
      console.error("Analytics API Error:", error);
      if (error?.response?.status === 401) {
        setIsUnauthorised(true);
      }
    } finally {
      setLoading(false);
    }
  };

  //  REPORTS API
  const fetchDriverReports = async (driverCode) => {
    if (!driverCode) return;

    setTaskLoading(true);
    try {
      const payload = {
        adminId: AdminId,
        driverCode,
        fromDate,
        toDate,
      };

      const res = await axios.post(
        `${baseurl}/api/Task/v1.0/getDriverReport`,
        payload,
        {
          headers: {
            Authorization: `Basic ${token}`,
          },
        }
      );

      setReports(res.data?.data || []);
    } catch (error) {
      console.error("Reports API Error:", error);
      if (error?.response?.status === 401) {
        setIsUnauthorised(true);
      }
    } finally {
      setTaskLoading(false);
    }
  };

  useEffect(() => {
    if (driverId) {
      fetchDriverAnalytics(driverId);
      fetchDriverReports(driverId);
    }
  }, [driverId]);

  const handleApply = () => {
    setCurrentPage(1);
    fetchDriverAnalytics(driverId);
    fetchDriverReports(driverId);
  };

  const reportsPerPage = 25;
  const [currentPage, setCurrentPage] = useState(1);

  const totalCount = reports.length;

  const indexOfLastReport = currentPage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;

  const currentReports = reports.slice(indexOfFirstReport, indexOfLastReport);

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen space-y-5">
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-md text-gray-800">
                Packky Driver ({decodeURIComponent(username || "")} ({driverId}
                ))
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-end justify-end">
            <div>
              <label className="block text-xs font-medium mb-1">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border p-2 rounded text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border p-2 rounded text-sm"
              />
            </div>

            <button
              onClick={handleApply}
              className="min-w-[96px] h-10 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm flex items-center justify-center"
            >
              Apply
            </button>

            {/* <button
              className="min-w-[96px] h-10 bg-gray-700 text-white rounded text-sm flex items-center justify-center"
              onClick={() => window.print()}
            >
              Export
            </button> */}

            <button
              className="min-w-[120px] h-10 bg-green-600 hover:bg-green-700 text-white rounded text-sm flex items-center justify-center gap-2"
              onClick={handleExport}
            >
              <FileDown size={16} />
              <span>Export Excel</span>
            </button>

            <button
              onClick={() => navigate(-1)}
              className="min-w-[96px] h-10 border rounded text-sm hover:bg-gray-50 flex items-center justify-center"
            >
              ← Back
            </button>
          </div>
        </div>

        <h2 className="text-xl font-semibold">Driver Analytics</h2>

        {loading && (
          <p className="text-sm text-gray-500">Loading analytics...</p>
        )}

        {!loading && analytics && (
          <div className="grid gap-3 md:grid-cols-5">
            <div className="group rounded-2xl border border-l-4 border-l-blue-500 bg-white p-4 transition-all hover:bg-blue-100 hover:border-l-blue-700">
              <div className="flex items-center gap-3">
                <ClipboardList className="h-6 w-6 text-blue-600 group-hover:text-blue-700" />
                <div>
                  <div className="text-xs text-gray-500">Assigned Orders</div>
                  <div className="text-lg font-semibold">
                    {showValue(analytics.assigned_orders)}
                  </div>
                </div>
              </div>
            </div>

            <div className="group rounded-2xl border border-l-4 border-l-green-500 bg-white p-4 transition-all hover:bg-green-100 hover:border-l-green-700">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-600 group-hover:text-green-700" />
                <div>
                  <div className="text-xs text-gray-500">Successful Orders</div>
                  <div className="text-lg font-semibold text-green-700">
                    {showValue(analytics.current_successful_orders)}
                  </div>
                </div>
              </div>
            </div>

            <div className="group rounded-2xl border border-l-4 border-l-red-500 bg-white p-4 transition-all hover:bg-red-100 hover:border-l-red-700">
              <div className="flex items-center gap-3">
                <XCircle className="h-6 w-6 text-red-600 group-hover:text-red-700" />
                <div>
                  <div className="text-xs text-gray-500">Declined Orders</div>
                  <div className="text-lg font-semibold text-red-600">
                    {showValue(analytics.declined_orders)}
                  </div>
                </div>
              </div>
            </div>

            <div className="group rounded-2xl border border-l-4 border-l-indigo-500 bg-white p-4 transition-all hover:bg-indigo-100 hover:border-l-indigo-700">
              <div className="flex items-center gap-3">
                <IndianRupee className="h-6 w-6 text-indigo-600 group-hover:text-indigo-700" />
                <div>
                  <div className="text-xs text-gray-500">Per Day Earning</div>
                  <div className="text-lg font-semibold text-indigo-700">
                    {showValue(analytics.per_day, true)}
                  </div>
                </div>
              </div>
            </div>

            <div className="group rounded-2xl border border-l-4 border-l-emerald-500 bg-white p-4 transition-all hover:bg-emerald-50 hover:border-l-emerald-600">
              <div className="flex items-center gap-3">
                <Wallet className="h-6 w-6 text-emerald-600 group-hover:text-emerald-700" />
                <div>
                  <div className="text-xs text-gray-500">Per Order Earning</div>
                  <div className="text-lg font-semibold text-emerald-700">
                    {showValue(analytics.per_order, true)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !analytics && (
          <p className="text-sm text-gray-400">
            No analytics data available for selected date range.
          </p>
        )}

        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div>
            <h2 className="text-xl font-semibold">Driver Reports</h2>
          </div>
        </div>

        {/* Reports Table */}
        <div className="bg-white border rounded-lg shadow-sm mt-6">
          {taskLoading ? (
            <p className="p-4 text-sm text-gray-500">Loading reports...</p>
          ) : reports.length === 0 ? (
            <p className="p-4 text-sm text-gray-400">
              No reports found for selected date range.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-orange-400 text-white">
                  <tr>
                    <th className="px-4 py-2 text-left">Task Id</th>
                    <th className="px-4 py-2 text-left">Order Id</th>
                    <th className="px-4 py-2 text-left">Description</th>
                    <th className="px-4 py-2 text-left">Estimate Time</th>
                    <th className="px-4 py-2 text-left">Distance</th>
                    <th className="px-4 py-2 text-left">Delivery Date</th>
                    <th className="px-4 py-2 text-left">Waiting Time</th>
                    <th className="px-4 py-2 text-left">Task Update</th>
                    <th className="px-4 py-2 text-left">Charges</th>
                    <th className="px-4 py-2 text-left">Delivery Time</th>
                  </tr>
                </thead>
                <tbody>
                  {currentReports.map((task, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-2">{task.pk_task_id}</td>
                      <td className="px-4 py-2">{task.order_id}</td>
                      <td className="px-4 py-2">{task.task_description}</td>
                      <td className="px-4 py-2">{fmt2(task.estimated_time)}</td>
                      <td className="px-4 py-2">{fmt2(task.distance)}</td>
                      <td className="px-4 py-2">
                        {task.delivery_date ?? "N/A"}
                      </td>
                      <td className="px-4 py-2">{fmt2(task.waiting_time)}</td>
                      <td className="px-4 py-2">{task.task_update ?? "N/A"}</td>
                      <td className="px-4 py-2">₹ {fmt2(task.charges)}</td>
                      <td className="px-4 py-2">
                        {task.delivery_time ?? "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePagination
                currentPage={currentPage}
                totalCount={totalCount}
                pageSize={reportsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DriverAnalytics;